import { isReference } from '@apollo/client';
import type { FetchResult } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { VitalSignObservationRecord } from '@models/vital-sign';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListFitnessObservationsQuery,
  usePostFitnessObservationRecordMutation,
  type PostFitnessObservationRecordMutationVariables,
} from 'generated/fitness.generated';

export const useGetFitnessObservations = (
  code?: string,
  skipQuery?: boolean,
  filterUnpublished?: boolean,
  dateRange?: DateRange
) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListFitnessObservationsQuery({
    variables: {
      input: {
        patient: patient_id,
        code,
        filterUnpublished,
        dateFrom: dateRange?.dateFrom,
        dateTo: dateRange?.dateTo,
      },
    },
    skip: !patient_id || Boolean(skipQuery),
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listFitnessObservations.data ?? []).map(
    (r) => new VitalSignObservationRecord(r)
  );
  return { ...response, data: records };
};

export function useCreateFitnessObservation(): [
  (
    params: PostFitnessObservationRecordMutationVariables
  ) => Promise<FetchResult<VitalSignObservationRecord>>,
  boolean,
] {
  const [mutate, { loading }] = usePostFitnessObservationRecordMutation();

  async function createFitnessObservation(
    params: PostFitnessObservationRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postFitnessObservationRecord) {
          return;
        }
        try {
          cache.modify<{ listFitnessObservations: CachedRecordList }>({
            fields: {
              listFitnessObservations: (
                existing,
                { storeFieldName, DELETE }
              ) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === params.input.subject?.reference) {
                  return DELETE;
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const created = response.data?.postFitnessObservationRecord;
    return {
      ...response,
      data: created ? new VitalSignObservationRecord(created) : undefined,
    };
  }

  return [createFitnessObservation, loading];
}
